import { useQuery } from "@tanstack/react-query";

import {
  IAdminUser,
  IPermissionCatalogItem,
  IRoleWithPermissions,
} from "../../../@types/admin/AdminAccess";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { accessManagementService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export function useRolesWithPermissions() {
  const { data, isLoading, isError } = useQuery<IRoleWithPermissions[]>({
    queryKey: ["admin-role-permissions-roles"],
    queryFn: async () =>
      ensureOk(await accessManagementService.getRolesWithPermissions()),
    refetchOnMount: "always",
  });

  return { roles: data ?? [], isLoading, isError };
}

export function useRolePermissionCatalog() {
  const { data, isLoading, isError } = useQuery<IPermissionCatalogItem[]>({
    queryKey: ["admin-role-permissions-catalog"],
    queryFn: async () =>
      ensureOk(await accessManagementService.getRolePermissionCatalog()),
  });

  return { permissionCatalog: data ?? [], isLoading, isError };
}

export function useAdminUsers(search: string, page: number, pageSize: number) {
  const { data, isLoading, isError, isFetching } = useQuery<
    IPaginatedResponse<IAdminUser[]>
  >({
    queryKey: ["admin-user-access-users", search, page, pageSize],
    queryFn: async () =>
      ensureOk(await accessManagementService.getUsers(search, page, pageSize)),
    refetchOnMount: "always",
  });

  return {
    users: data?.result ?? [],
    pagination: {
      currentPage: data?.currentPage ?? page,
      pageSize: data?.pageSize ?? pageSize,
      totalPages: data?.pages ?? 0,
      totalItems: data?.totalCount ?? 0,
      hasPreviousPage: data?.hasPreviousPage ?? false,
      hasNextPage: data?.hasNextPage ?? false,
    },
    isLoading,
    isError,
    isFetching,
  };
}

export function useUserAccessRoles() {
  const { data, isLoading, isError } = useQuery<IRoleWithPermissions[]>({
    queryKey: ["admin-user-access-roles"],
    queryFn: async () =>
      ensureOk(await accessManagementService.getUserAccessRoles()),
  });

  return { roles: data ?? [], isLoading, isError };
}

export function useUserAccessPermissionCatalog() {
  const { data, isLoading, isError } = useQuery<IPermissionCatalogItem[]>({
    queryKey: ["admin-user-access-catalog"],
    queryFn: async () =>
      ensureOk(await accessManagementService.getUserAccessPermissionCatalog()),
  });

  return { permissionCatalog: data ?? [], isLoading, isError };
}

export function useUserDirectPermissions(cmsUserId: number) {
  const { data, isLoading, isError } = useQuery<string[]>({
    queryKey: ["admin-user-access-user-permissions", cmsUserId],
    queryFn: async () =>
      ensureOk(
        await accessManagementService.getUserDirectPermissions(cmsUserId)
      ),
    refetchOnMount: "always",
  });

  return { directPermissions: data ?? [], isLoading, isError };
}
